/**
 * useDistrictOperational
 *
 * Loads district-level operational intelligence:
 * church risk evaluations, pastoral signals, actionable priorities,
 * and a merged operational feed — all from 4 parallel fetches.
 *
 * No N+1: per-church recency/counts computed from flat district visit array.
 * Graceful degradation: Promise.allSettled — partial failures produce partial data.
 * All heavy computation runs in .then() — zero sorting in render.
 */

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  getChurches,
  getVisits,
  getPrayers,
  getChurchEventsByDistrict,
} from "@/lib/api";
import { getLocalDateString } from "@/lib/date";
import {
  computeChurchVisitStats,
  evaluateChurchesForDistrict,
  buildDistrictSignals,
  buildDistrictPriorities,
  buildDistrictFeed,
  buildDistrictWorkflows,
} from "@/lib/district-operational";
import { groupFeedByContext } from "@/lib/pastoral-feed";
import {
  estimatePastoralLoad,
  buildSuggestedSchedule,
} from "@/lib/pastoral-scheduling";
import { buildDistrictNarrative } from "@/lib/pastoral-narrative";
import { buildDistrictExecution } from "@/lib/pastoral-execution";
import { buildDistrictPastoralState } from "@/lib/pastoral-state";
import { buildDistrictAssistantContext } from "@/lib/assistant-context";

const EMPTY = {
  loading:           true,
  churchEvaluations: [],
  signals:           [],
  priorities:        [],
  feedGroups:        [],
  districtWorkflows:  [],
  load:               null,
  districtNarrative:  null,
  districtExecution:      null,
  districtPastoralState:    null,
  districtAssistantContext: null,
  schedule:                 [],
};

export function useDistrictOperational(districtId) {
  const nav = useNavigate();
  const [state, setState] = useState(EMPTY);

  useEffect(() => {
    if (!districtId) {
      setState({ ...EMPTY, loading: false });
      return;
    }

    let cancelled = false;
    const today = getLocalDateString();

    Promise.allSettled([
      getChurches(districtId),
      getVisits({ district_id: districtId }),
      getPrayers({ district_id: districtId, status: "activa" }),
      getChurchEventsByDistrict({ district_id: districtId, from_date: today }),
    ]).then(([churchRes, visitRes, prayerRes, eventRes]) => {
      if (cancelled) return;

      const churches = churchRes.status === "fulfilled" ? churchRes.value || [] : [];
      const visits   = visitRes.status  === "fulfilled" ? visitRes.value  || [] : [];
      const prayers  = prayerRes.status === "fulfilled" ? prayerRes.value || [] : [];
      const events   = eventRes.status  === "fulfilled" ? eventRes.value  || [] : [];

      const { visitRecencyMap, visitCountMap } = computeChurchVisitStats(visits);

      const churchEvaluations = evaluateChurchesForDistrict(churches, {
        visitRecencyMap,
        visitCountMap,
      });

      const signals          = buildDistrictSignals(churchEvaluations, prayers);
      const priorities       = buildDistrictPriorities(signals);
      const feedItems        = buildDistrictFeed(churchEvaluations, prayers, events, { nav });
      const feedGroups       = groupFeedByContext(feedItems);

      // Scheduling layer — all computed here, zero work in render
      const districtWorkflows = buildDistrictWorkflows(churchEvaluations);
      const load              = estimatePastoralLoad(districtWorkflows);
      const districtNarrative      = buildDistrictNarrative(districtWorkflows, load, prayers);
      const districtExecution      = buildDistrictExecution(churchEvaluations);
      const schedule               = buildSuggestedSchedule(districtWorkflows, prayers);
      const districtPastoralState  = buildDistrictPastoralState({
        districtWorkflows,
        districtNarrative,
        districtExecution,
        load,
        schedule,
        signals,
      });

      const districtAssistantContext = buildDistrictAssistantContext(districtPastoralState);

      if (process.env.NODE_ENV === "development") {
        console.debug("[PastoralState:district]",   districtPastoralState);
        console.debug("[AssistantContext:district]", districtAssistantContext);
      }

      setState({
        loading: false,
        churchEvaluations,
        signals,
        priorities,
        feedGroups,
        districtWorkflows,
        load,
        districtNarrative,
        districtExecution,
        districtPastoralState,
        districtAssistantContext,
        schedule,
      });
    });

    return () => { cancelled = true; };
  }, [districtId, nav]);

  return state;
}
